import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AudioOutlined, LoadingOutlined, StopOutlined } from '@ant-design/icons';
import { Button, Input, message, Tooltip } from 'antd';
import { getCore } from 'cvat-core-wrapper';

type VoiceTargetScope = 'object' | 'job';

interface VoiceFieldContext {
    jobId: number;
    phase: string;
    attributeSpecId: number;
    attributeName: string;
    targetScope: VoiceTargetScope;
    targetServerId?: number | null;
    modelName?: string;
}

interface Props {
    value: string;
    onChange: (value: string) => void;
    context: VoiceFieldContext;
    disabled?: boolean;
    placeholder?: string;
}

const core = getCore();
const CHUNK_TIMESLICE_MS = 1800;
const { TextArea } = Input;

function getPreferredMimeType(): string | undefined {
    if (typeof MediaRecorder === 'undefined') {
        return undefined;
    }

    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function mergeText(base: string, delta: string): string {
    const trimmedBase = (base || '').trim();
    const trimmedDelta = (delta || '').trim();
    if (!trimmedDelta) {
        return trimmedBase;
    }

    if (!trimmedBase) {
        return trimmedDelta;
    }

    return `${trimmedBase} ${trimmedDelta}`.replace(/\s+/g, ' ').trim();
}

export default function AnnotationVoiceInput({
    value,
    onChange,
    context,
    disabled = false,
    placeholder,
}: Props): JSX.Element {
    const [recording, setRecording] = useState(false);
    const [loading, setLoading] = useState(false);
    const [recordId, setRecordId] = useState<number | null>(null);
    const [isTextUnlocked, setIsTextUnlocked] = useState(Boolean((value || '').trim()));

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);
    const chunkIndexRef = useRef(0);
    const chunkErrorShownRef = useRef(false);
    const chunkUploadStateRef = useRef<{
        inFlight: boolean;
        latest: { blob: Blob; index: number } | null;
    }>({ inFlight: false, latest: null });
    const sessionBaseTextRef = useRef('');
    const lockInfoShownRef = useRef(false);
    const currentValueRef = useRef(value || '');
    const rawTranscriptRef = useRef('');
    const saveDebounceRef = useRef<number | null>(null);

    useEffect(() => {
        currentValueRef.current = value || '';
        const hasText = Boolean((value || '').trim());

        // Keep empty fields mic-locked; unlock only when there is transcript/text.
        // While actively recording, avoid flickering the lock during in-flight updates.
        if (recording && !hasText) {
            return;
        }

        setIsTextUnlocked(hasText);
    }, [recording, value]);

    const apiBaseUrl = `${core.config.backendAPI}/jobs/${context.jobId}/voice-transcriptions`;

    const clearDebouncedSave = useCallback(() => {
        if (saveDebounceRef.current !== null) {
            window.clearTimeout(saveDebounceRef.current);
            saveDebounceRef.current = null;
        }
    }, []);

    const patchEditedText = useCallback(async (id: number, editedText: string) => {
        try {
            await core.server.request(`${apiBaseUrl}/${id}`, {
                method: 'PATCH',
                data: { edited_text: editedText },
                timeout: 30000,
            });
        } catch (error) {
            // Keep UI responsive even if persistence fails; user can continue editing.
            // eslint-disable-next-line no-console
            console.error(error);
        }
    }, [apiBaseUrl]);

    const clearFieldVoiceHistory = useCallback(async (): Promise<void> => {
        try {
            await core.server.request(`${apiBaseUrl}/clear`, {
                method: 'POST',
                data: {
                    attribute_spec_id: context.attributeSpecId,
                    target_scope: context.targetScope,
                    target_server_id: context.targetScope === 'object' ? context.targetServerId : null,
                },
                timeout: 30000,
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            message.error('Failed to clear saved voice snippets for this field.');
        } finally {
            setRecordId(null);
            rawTranscriptRef.current = '';
            sessionBaseTextRef.current = '';
            lockInfoShownRef.current = false;
        }
    }, [apiBaseUrl, context.attributeSpecId, context.targetScope, context.targetServerId]);

    const debouncedPatchEditedText = useCallback((id: number, editedText: string) => {
        clearDebouncedSave();
        saveDebounceRef.current = window.setTimeout(() => {
            patchEditedText(id, editedText);
        }, 800);
    }, [clearDebouncedSave, patchEditedText]);

    const pushChunk = useCallback(async (id: number, chunk: Blob, chunkIndex: number): Promise<void> => {
        const formData = new FormData();
        formData.append('audio', chunk, `chunk-${chunkIndex}.webm`);
        formData.append('chunk_index', String(chunkIndex));
        formData.append('edited_text', currentValueRef.current);

        const response = await core.server.request(`${apiBaseUrl}/${id}/chunk`, {
            method: 'POST',
            data: formData,
            timeout: 60000,
        });

        const cumulativeTranscript = String(response.data?.raw_transcript || '').trim();
        const deltaText = String(response.data?.delta_text || '').trim();

        if (cumulativeTranscript) {
            rawTranscriptRef.current = cumulativeTranscript;
            const merged = mergeText(sessionBaseTextRef.current, cumulativeTranscript);
            if (merged !== currentValueRef.current) {
                currentValueRef.current = merged;
                onChange(merged);
                setIsTextUnlocked(true);
                debouncedPatchEditedText(id, merged);
            }
            return;
        }

        if (deltaText) {
            const merged = mergeText(sessionBaseTextRef.current, mergeText(rawTranscriptRef.current, deltaText));
            rawTranscriptRef.current = mergeText(rawTranscriptRef.current, deltaText);
            currentValueRef.current = merged;
            onChange(merged);
            setIsTextUnlocked(true);
            debouncedPatchEditedText(id, merged);
        }
    }, [apiBaseUrl, onChange, debouncedPatchEditedText]);

    const flushLatestChunk = useCallback((id: number): void => {
        if (chunkUploadStateRef.current.inFlight) {
            return;
        }

        const next = chunkUploadStateRef.current.latest;
        if (!next) {
            return;
        }

        chunkUploadStateRef.current.latest = null;
        chunkUploadStateRef.current.inFlight = true;

        pushChunk(id, next.blob, next.index)
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error);
                if (!chunkErrorShownRef.current) {
                    chunkErrorShownRef.current = true;
                    message.error('Live voice transcription failed for one of the audio chunks.');
                }
            })
            .finally(() => {
                chunkUploadStateRef.current.inFlight = false;
                if (chunkUploadStateRef.current.latest) {
                    flushLatestChunk(id);
                }
            });
    }, [pushChunk]);

    const startRecording = useCallback(async () => {
        if (disabled || recording || loading) {
            return;
        }

        if (!navigator?.mediaDevices?.getUserMedia) {
            message.error('Microphone is not available in this browser.');
            return;
        }

        setLoading(true);
        try {
            const sessionResponse = await core.server.request(`${apiBaseUrl}/start`, {
                method: 'POST',
                data: {
                    attribute_spec_id: context.attributeSpecId,
                    attribute_name: context.attributeName,
                    target_scope: context.targetScope,
                    target_server_id: context.targetServerId,
                    phase: context.phase,
                    model_name: context.modelName || 'UsefulSensors/moonshine-streaming-medium',
                },
                timeout: 30000,
            });

            const startedRecordId = Number(sessionResponse.data?.id);
            if (!Number.isInteger(startedRecordId) || startedRecordId <= 0) {
                throw new Error('Voice transcription session was not created.');
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mimeType = getPreferredMimeType();
            const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

            chunksRef.current = [];
            chunkIndexRef.current = 0;
            chunkErrorShownRef.current = false;
            rawTranscriptRef.current = '';
            chunkUploadStateRef.current = { inFlight: false, latest: null };
            sessionBaseTextRef.current = (currentValueRef.current || '').trim();
            lockInfoShownRef.current = false;

            recorder.ondataavailable = (event: BlobEvent) => {
                if (!event.data || !event.data.size) {
                    return;
                }

                const chunkCopy = event.data;
                chunksRef.current.push(chunkCopy);
                const idx = chunkIndexRef.current++;
                const cumulativeAudio = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });

                chunkUploadStateRef.current.latest = {
                    blob: cumulativeAudio,
                    index: idx,
                };
                flushLatestChunk(startedRecordId);
            };

            recorder.onerror = () => {
                message.error('Voice recorder failed unexpectedly.');
            };

            recorder.start(CHUNK_TIMESLICE_MS);
            mediaRecorderRef.current = recorder;
            setRecordId(startedRecordId);
            setRecording(true);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            message.error('Unable to start voice recording.');
        } finally {
            setLoading(false);
        }
    }, [apiBaseUrl, context, disabled, flushLatestChunk, loading, recording]);

    const stopRecording = useCallback(async () => {
        if (!recording || !recordId) {
            return;
        }

        const recorder = mediaRecorderRef.current;
        if (!recorder) {
            return;
        }

        setRecording(false);
        setLoading(true);

        try {
            await new Promise<void>((resolve) => {
                let settled = false;
                const finalize = () => {
                    if (!settled) {
                        settled = true;
                        resolve();
                    }
                };

                recorder.onstop = () => finalize();

                try {
                    if (recorder.state !== 'inactive') {
                        recorder.stop();
                    } else {
                        finalize();
                    }
                } catch {
                    finalize();
                }

                // Ensure the UI never remains in loading state if onstop is not emitted.
                window.setTimeout(finalize, 1500);
            });

            await new Promise<void>((resolve) => {
                const waitUntilChunksSynced = () => {
                    const { inFlight, latest } = chunkUploadStateRef.current;
                    if (!inFlight && !latest) {
                        resolve();
                        return;
                    }

                    if (!inFlight && latest) {
                        flushLatestChunk(recordId);
                    }
                    window.setTimeout(waitUntilChunksSynced, 120);
                };

                waitUntilChunksSynced();
            });

            const fullAudio = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
            const formData = new FormData();
            formData.append('audio', fullAudio, 'voice-note.webm');
            formData.append('edited_text', currentValueRef.current);
            formData.append('raw_transcript', rawTranscriptRef.current);

            await core.server.request(`${apiBaseUrl}/${recordId}/finish`, {
                method: 'POST',
                data: formData,
                timeout: 60000,
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
            message.error('Unable to finalize voice transcription.');
        } finally {
            mediaRecorderRef.current = null;
            chunksRef.current = [];
            streamRef.current?.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
            setLoading(false);
        }
    }, [apiBaseUrl, flushLatestChunk, recordId, recording]);

    useEffect(() => () => {
        clearDebouncedSave();
        try {
            mediaRecorderRef.current?.stop();
        } catch {
            // no-op
        }
        streamRef.current?.getTracks().forEach((track) => track.stop());
    }, [clearDebouncedSave]);

    const voiceLocked = !isTextUnlocked && !recording;
    const micTooltip = recording
        ? 'Stop microphone'
        : voiceLocked
            ? 'Step 1: use microphone to unlock typing'
            : 'Start microphone';

    return (
        <>
            <div className='cvat-annotation-voice-input'>
                <TextArea
                    className='cvat-annotation-voice-input-textarea'
                    value={value}
                    disabled={disabled}
                    readOnly={recording || !isTextUnlocked}
                    placeholder={voiceLocked ? 'Voice input required first. Click microphone to start.' : placeholder}
                    autoSize={{ minRows: 2, maxRows: 10 }}
                    style={voiceLocked ? { backgroundColor: '#fafafa', borderStyle: 'dashed' } : undefined}
                    onFocus={() => {
                        if (voiceLocked && !lockInfoShownRef.current) {
                            lockInfoShownRef.current = true;
                            message.info('Use the microphone first, then edit the text if needed.');
                        }
                    }}
                    onChange={(event) => {
                        if (!isTextUnlocked) {
                            return;
                        }
                        const previousValue = currentValueRef.current || '';
                        const nextValue = event.target.value;
                        currentValueRef.current = nextValue;
                        onChange(nextValue);

                        if (!nextValue.trim() && previousValue.trim()) {
                            clearDebouncedSave();
                            void clearFieldVoiceHistory();
                            return;
                        }

                        if (recordId) {
                            debouncedPatchEditedText(recordId, nextValue);
                        }
                    }}
                    onBlur={() => {
                        if (recordId) {
                            clearDebouncedSave();
                            patchEditedText(recordId, currentValueRef.current);
                        }
                    }}
                />
                <Tooltip title={loading ? 'Working...' : micTooltip}>
                    <Button
                        className='cvat-annotation-voice-input-mic'
                        type={recording || voiceLocked ? 'primary' : 'default'}
                        icon={loading ? <LoadingOutlined /> : recording ? <StopOutlined /> : <AudioOutlined />}
                        onClick={recording ? stopRecording : startRecording}
                        disabled={disabled || loading}
                    />
                </Tooltip>
            </div>
            {voiceLocked && (
                <div className='cvat-annotation-voice-input-hint'>
                    Step 1: speak with the microphone. Step 2: edit the transcribed text.
                </div>
            )}
        </>
    );
}
